import React, { Fragment, useEffect, useState } from 'react';
import ABHAOtpBox from './ABHAOtpBox';
import PatientPhoneAutoSuggestBlock from '../PatientPhoneAutoSuggestBlock';
import { Patient } from '../../store/types/patient';
import { IHealthId } from '../../api/abha/confirm-otp';
import { generateAbhaOtp } from '../../api/abha/generate-otp';
import { AbhaIconNotLinked, rightArrowIcon, rightArrowIcon2 } from '../common/Icons';
import { notification } from 'antd';
import { captureError } from '../../pages/captureError';
import { track } from '../../tracking';
import { IFormState } from '../common/types/add-patient';
import { checkIfValidPhoneNumber } from '../../pages/utils';
import { useParams } from 'react-router-dom';
import { Gender } from '../../pages/NewPatient';
import { getMe, getMySingleDoctorId, getSelectedClinicId } from '../../store/idbHandler';

interface Props {
    phCode: string;
    gender: Gender | undefined;
    phoneNumber: string | undefined;
    onPhoneNumberChange: (input?: string) => void;
    onAutoComplete: (patient?: Patient) => void;
    name: string | undefined;
    isPhoneNumberError: boolean;
    otp: string;
    setOtpHandler: (otpValue: string) => void;
    setGenerateOtpStateHandler: (otpValue: 'none' | 'loading' | 'success' | 'error') => void;
    generateOtpState: 'error' | 'none' | 'loading' | 'success';
    setTxnIdHandler: (txnId?: string) => void;
    txnId: string | undefined;
    setHealthIdHandler: (healthIds: IHealthId[] | null) => void;
    healthIds: IHealthId[] | null;
    confirmState: 'none' | 'loading' | 'error' | 'options' | 'no-options' | 'linking';
    setConfirmStateHandler: (
        val: 'none' | 'loading' | 'error' | 'options' | 'no-options' | 'linking',
    ) => void;
    selectedHealthIdHandler: (val: IHealthId | null) => void;
    selectedHealthId: IHealthId | null;
    setErrorStringHandler: (val: string | undefined) => void;
    setIsOtpDisabledHandler: (val: boolean) => void;
    isOtpDisabled: boolean;
    errorString: string | undefined;
    isModal: boolean;
    patientId: string | undefined;
    authHealthId: string | undefined;
    showSendOtpScreen: boolean;
    setShowSendOtpScreen: (val: boolean) => void;
    setAbhaFields: (opt: { skipState: string; abhaId: string | undefined }) => void;
    setAddPtFormState?: (formstate: IFormState | undefined) => void;
    abhaId: string | undefined;
    setIsNameDisabled?: (val: boolean) => void;
    setIsPhoneNumberError: (val: boolean) => void;
    createFlow: (skipState?: string) => void;
    resetError: () => void;
    setAuthHealthId: (val: string | undefined) => void;
    abhaLinkPatient?: Patient;
    closeModal?: () => void;
    onAbhaLink?: (pt: Patient) => void;
    fromNdhmDiscovery: boolean | undefined;
    cId?: string;
}

const PhoneNoOtpComponent: React.FC<Props> = ({
    phCode,
    gender,
    onPhoneNumberChange,
    onAutoComplete,
    name,
    phoneNumber,
    isPhoneNumberError,
    otp,
    setOtpHandler,
    setGenerateOtpStateHandler,
    generateOtpState,
    setTxnIdHandler,
    txnId,
    setHealthIdHandler,
    healthIds,
    confirmState,
    setConfirmStateHandler,
    selectedHealthIdHandler,
    selectedHealthId,
    setErrorStringHandler,
    setIsOtpDisabledHandler,
    isOtpDisabled,
    errorString,
    isModal,
    patientId,
    authHealthId,
    showSendOtpScreen,
    setShowSendOtpScreen,
    setAbhaFields,
    setAddPtFormState,
    abhaId,
    setIsNameDisabled,
    setIsPhoneNumberError,
    createFlow,
    resetError,
    setAuthHealthId,
    abhaLinkPatient,
    closeModal,
    onAbhaLink,
    fromNdhmDiscovery,
    cId,
}) => {
    const [phDisabled, setPhDisabled] = useState(false);
    const params: { cId: string } = useParams();

    const gotoInitialState = () => {
        setConfirmStateHandler('none');
        setGenerateOtpStateHandler('none');
        setOtpHandler('');
        setTxnIdHandler(undefined);
        selectedHealthIdHandler(null);
        setHealthIdHandler(null);
        setErrorStringHandler(undefined);
        setIsOtpDisabledHandler(true);
        setIsNameDisabled && setIsNameDisabled(false);
    };

    const generateOtp = async () => {
        const mobile = phoneNumber;

        try {
            track('GENERATE_OTP', { authHealthId, abhaId, phoneNumber, patientId });

            if (mobile?.length === 10 && !abhaId) {
                setShowSendOtpScreen && setShowSendOtpScreen(false);
                setGenerateOtpStateHandler('loading');
                setPhDisabled(true);

                track('GENERATE_OTP_TRIGGERED', {
                    authHealthId,
                    mobile,
                    patientId,
                    abhaLinkPatient: abhaLinkPatient?.id ?? '',
                });

                const res = await generateAbhaOtp(
                    getMySingleDoctorId() || '',
                    cId || params.cId || getSelectedClinicId() || '',
                    getMe()?.business?.id || '',
                    {
                        mobile,
                        health_id: fromNdhmDiscovery ? undefined : authHealthId ?? undefined,
                        pid: patientId ?? undefined,
                    },
                );

                if (res?.error) {
                    throw Error(res?.error);
                }

                track('GENERATE_OTP_SUCCESSFULL', {
                    authHealthId,
                    mobile,
                    patientId,
                    abhaLinkPatient: abhaLinkPatient?.id ?? '',
                });

                res.data.healthId && setAuthHealthId(res.data.healthId);

                if (!res.data.txn_id) {
                    setAbhaFields({ skipState: 'abha_end', abhaId: res.data.healthId });
                    setAddPtFormState && setAddPtFormState('add-pt');
                }

                setPhDisabled(false);
                setIsOtpDisabledHandler(false);
                setTxnIdHandler && setTxnIdHandler(res.data.txn_id);
                setGenerateOtpStateHandler('success');
            }
        } catch (e: any) {
            track('GENERATE_OTP_ERROR', {
                authHealthId,
                abhaId,
                mobile,
                patientId,
                health_id: authHealthId ?? undefined,
                pid: patientId ?? undefined,
                error: e?.message,
                abhaLinkPatient: abhaLinkPatient?.id ?? '',
            });

            setPhDisabled(false);
            setIsOtpDisabledHandler(true);
            setErrorStringHandler((e as Error)?.message ?? 'Something went wrong!');
            setGenerateOtpStateHandler('error');
            notification.error({ message: e?.message ?? 'Something wrong happened', duration: 5 });
            // captureError(new Error(`ABHA: ${e?.message}`));
        }
    };

    useEffect(() => {
        if (phoneNumber?.length === 10) {
            if (!checkIfValidPhoneNumber(phCode, phoneNumber)) {
                setIsPhoneNumberError(true);
                setShowSendOtpScreen && setShowSendOtpScreen(false);
                setIsOtpDisabledHandler(true);
            } else {
                if (abhaLinkPatient) {
                    generateOtp();
                } else {
                    setShowSendOtpScreen && setShowSendOtpScreen(true);
                }
            }
        } else {
            setIsPhoneNumberError(false);
            gotoInitialState();
            setShowSendOtpScreen && setShowSendOtpScreen(false);
        }
    }, [phoneNumber]);

    return (
        <Fragment>
            <div className="flex flex-col w-full gap-12 items-start">
                {isModal && <p className="text-13 font-700">Mobile Number *</p>}
                <PatientPhoneAutoSuggestBlock
                    phCode={phCode}
                    onPhoneNumberChange={onPhoneNumberChange}
                    onAutoComplete={onAutoComplete}
                    suggestionsEnabled={true}
                    disabled={abhaLinkPatient ? true : phDisabled}
                    phoneNumber={phoneNumber ?? ''}
                    isPhoneNumberError={isPhoneNumberError}
                    isModal={isModal}
                    cid={params.cId}
                    isAbdmEnabled={true}
                    fromNdhmDiscovery={fromNdhmDiscovery}
                />
            </div>
            {fromNdhmDiscovery && name && phoneNumber?.length === 10 && gender ? (
                <div
                    className="rounded-8 bg-darwin-neutral_new-50 px-10 py-8 gap-4 flex items-center"
                    style={{ width: 'fit-content' }}
                >
                    <span className="self-start">{AbhaIconNotLinked}</span>
                    <div className="flex flex-col gap-4">
                        <p className="text-13 font-700">
                            {name} | {gender?.slice(0, 1)}
                        </p>
                        <p className="text-12 font-400">
                            {phoneNumber} | {authHealthId}
                        </p>
                    </div>
                </div>
            ) : null}
            {showSendOtpScreen ? (
                <div className="flex items-start flex-col gap-12">
                    <p className="font-700 text-13">Send OTP to create/link ABHA</p>
                    <div
                        onClick={() => generateOtp()}
                        className="rounded-4 flex items-center justify-center gap-4 cursor-pointer bg-darwin-accent-bright-blue-800 px-8 py-6"
                    >
                        <p className="text-13 font-700 text-white">Send OTP</p>
                        {rightArrowIcon2('fill-white font-700 w-12 h-16')}
                    </div>
                </div>
            ) : (
                <ABHAOtpBox
                    otp={otp}
                    setOtp={setOtpHandler}
                    setGenerateOtpState={setGenerateOtpStateHandler}
                    generateOtpState={generateOtpState}
                    phoneNumber={phoneNumber}
                    setTxnId={setTxnIdHandler}
                    txnId={txnId}
                    setHealthIdHandler={setHealthIdHandler}
                    healthIds={healthIds}
                    confirmState={confirmState}
                    setConfirmStateHandler={setConfirmStateHandler}
                    selectedHealthIdHandler={selectedHealthIdHandler}
                    selectedHealthId={selectedHealthId}
                    setErrorString={setErrorStringHandler}
                    setIsOtpDisabled={setIsOtpDisabledHandler}
                    isOtpDisabled={isOtpDisabled}
                    errorString={errorString}
                    isModal={isModal}
                    togglePhoneNumber={(val: boolean) => setPhDisabled(val)}
                    key={txnId}
                    patientId={patientId}
                    setIsNameDisabled={setIsNameDisabled}
                    createFlow={createFlow}
                    resetError={resetError}
                    abhaLinkPatient={abhaLinkPatient}
                    closeModal={closeModal}
                    onAbhaLink={onAbhaLink}
                />
            )}
        </Fragment>
    );
};

export default PhoneNoOtpComponent;
